import { live } from './data/live';
import { breakIncentivesRenderer } from './renderers/break-incentives-renderer';

/**
 * The break screen's ad/incentive carousel: `ad → every incentive → next ad → …`,
 * forever, independent of the infobar cycle.
 *
 * It used to drive itself — showAdBreak() called startBreakIncentiveLoop(), which called
 * showAdBreak() back — which had two consequences worth remembering:
 *
 *  - starting it twice created two chains racing over the same two elements, and it was
 *    being started on every schedule change (measured 1, 2, 4, 6 concurrent runs over
 *    four run changes);
 *  - with no ads *and* no incentives it became a `setTimeout(…, 0)` busy loop, because
 *    the delay before the next ad was `incentives.length * step` and that is zero.
 *
 * Both are structural, so both are fixed here rather than patched: one driver, started
 * once, with a floor delay for the nothing-to-show case.
 */

const IDLE_RECHECK_MS = 5_000;

/** Resolves after `ms` of the page's own clock. */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const breakCarousel = {
    /** 'idle' | 'ad' | 'incentive' | 'empty'. Readable from the console. */
    state: 'idle',
    running: false,
    adIndex: 0,

    /** Safe to call on every schedule refresh: only the first call does anything. */
    start: () => {
        if (breakCarousel.running) return;
        breakCarousel.running = true;
        breakCarousel.loop();
    },

    stop: () => {
        breakCarousel.running = false;
        breakCarousel.state = 'idle';
    },

    loop: async () => {
        while (breakCarousel.running) {
            const ads = live.getBreakScreenAds();
            const incentives = live.incentives ?? [];

            if (ads.length === 0 && incentives.length === 0) {
                // Nothing configured yet. Idle politely instead of spinning.
                breakCarousel.state = 'empty';
                await wait(IDLE_RECHECK_MS);
                continue;
            }

            if (ads.length > 0) {
                breakCarousel.state = 'ad';
                if (breakCarousel.adIndex >= ads.length) breakCarousel.adIndex = 0;
                breakIncentivesRenderer.currentAdBreakIndex = breakCarousel.adIndex;
                await breakIncentivesRenderer.playAd(breakCarousel.adIndex);
                breakCarousel.adIndex = (breakCarousel.adIndex + 1) % ads.length;
            }

            // Re-read each time: a poll can add, remove or reorder incentives mid-cycle.
            for (const incentive of live.incentives ?? []) {
                if (!breakCarousel.running) return;
                breakCarousel.state = 'incentive';
                await breakIncentivesRenderer.playBreakIncentive(incentive);
                await wait(1_500); // the gap the old loop left between incentives
            }
        }
    },
};

window.breakCarousel = breakCarousel;
